import type { components } from '@pba/types-openapi-client';
import { CategoryListItem } from './category';
import type { Entity } from './entity';
import { type GroupExtended, createGroupExtended } from './group';
import { type Image, createImage } from './image';
import type { OrganizationPrivacy } from './privacy';
import type {
  OrganizationDetailDataObject,
  OrganizationSettingsDataObject,
  OrganizationSlimDataObject,
} from './service';
import type { Tag } from './tag';
import { type UploadedFile, createUploadedFile } from './uploaded-file';

// TODO: what is optional

export type OrganizationSettings = {
  allowedIntegrations?: string[];
  allowMedicalCommunity?: boolean;
};

export const createOrganizationSettings = (data: OrganizationSettingsDataObject): OrganizationSettings => ({
  allowedIntegrations: data.allowed_integrations,
  allowMedicalCommunity: data.allow_medical_community,
});

export type OrganizationSlim = {
  customChannels: boolean;
  intId: number;
} & Entity;

export const createOrganizationSlim = (data: OrganizationSlimDataObject): OrganizationSlim => ({
  customChannels: data.custom_channels,
  id: data.id,
  intId: data.intId,
  name: data.name,
});

export type OrganizationBase = {
  city?: string;
  landscapeLogo?: Image;
  profilePhoto?: Image;
  squareLogo?: Image;
  state?: string;
  whiteProfilePhoto?: Image;
} & OrganizationSlim;

export const createOrganizationBase = (data: components['schemas']['OrganizationBase']): OrganizationBase => ({
  // eslint-disable-next-line @typescript-eslint/no-misused-spread -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
  ...createOrganizationSlim(data),
  city: data.city,
  landscapeLogo: data.landscape_logo ? createImage(data.landscape_logo) : undefined,
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
  profilePhoto: data.profile_photo ? createImage(data.profile_photo) : undefined,
  squareLogo: data.square_logo ? createImage(data.square_logo) : undefined,
  state: data.state,
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
  whiteProfilePhoto: data.white_profile_photo ? createImage(data.white_profile_photo) : undefined,
});

export type OrganizationList = {
  categories: CategoryListItem[];
  coverPhoto?: UploadedFile;
  description: string;
  groupId?: null | string;
  isDefault: boolean;
  membersCount: number;
  privacy: OrganizationPrivacy;
  requiredConsents: Array<{ id: string }>;
  settings: OrganizationSettings;
  tags: Tag[];
  url: string;
  usersCount: number;
  // TODO other?
} & OrganizationBase;

export const createOrganizationList = (data: components['schemas']['OrganizationList']): OrganizationList => ({
  // eslint-disable-next-line @typescript-eslint/no-misused-spread -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
  ...createOrganizationBase(data),
  categories: data.categories.map((category) => new CategoryListItem(category)),
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
  coverPhoto: data.cover_photo ? createUploadedFile(data.cover_photo) : undefined,
  description: data.description,
  groupId: data.group_id ?? undefined,
  isDefault: data.is_default,
  membersCount: data.members_count,
  privacy: data.privacy,
  requiredConsents: data.required_consents,
  settings: createOrganizationSettings(data.settings),
  tags: data.tags,
  url: data.url,
  usersCount: data.users_count,
});

export type OrganizationDetail = {
  description: string;
  group?: GroupExtended;
  htmlDescription: string;
  // TODO other?
} & OrganizationList;

export const createOrganizationDetail = (data: OrganizationDetailDataObject): OrganizationDetail => ({
  // eslint-disable-next-line @typescript-eslint/no-misused-spread -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
  ...createOrganizationList(data),
  description: data.description,
  group: data.group ? createGroupExtended(data.group) : undefined,
  htmlDescription: data.html_description,
});

export const isChurch = ({ organization }: { organization: OrganizationList }) => Boolean(organization.groupId);
